// Pure HUD helpers: formatting, labels, angle mapping, scoreboard ordering. No DOM access.

import type { MatchHudView, ScoreboardRow, TeamTag } from "./types";

const DEG = Math.PI / 180;
const TAU = Math.PI * 2;
/** `hFovDeg` is defined at 16:9 (the renderer derives the vertical FOV from it). */
const REF_ASPECT = 16 / 9;

/** Remaining time as "m:ss" (rounded up: a countdown shows 0:00 only when it is over). null -> "--:--". */
export function formatTimer(sec: number | null | undefined): string {
  if (sec == null) return "--:--";
  const s = Number.isFinite(sec) ? Math.max(0, Math.ceil(sec)) : 0;
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, "0")}`;
}

export function modeLabel(mode: MatchHudView["mode"]): string {
  switch (mode) {
    case "ffa":
      return "FREE FOR ALL";
    case "tdm":
      return "TEAM DEATHMATCH";
    case "koth":
      return "KING OF THE HILL";
    case "defusal":
      return "DEFUSAL";
  }
}

/** Small label under the match timer. `timeLeftSec` overrides `m.timeLeftSec` (locally ticked clock). */
export function phaseLabel(m: MatchHudView, timeLeftSec: number | null = m.timeLeftSec): string {
  const round = m.round != null && m.round > 0 ? `ROUND ${m.round}` : "";
  switch (m.phase) {
    case "warmup":
      return "WARMUP";
    case "countdown":
      return timeLeftSec == null ? "STARTING" : `STARTS IN ${formatTimer(timeLeftSec)}`;
    case "live":
      return m.scoreLimit > 0 ? `FIRST TO ${m.scoreLimit}` : modeLabel(m.mode);
    case "round_freeze":
      return round ? `${round} \u00b7 FREEZE` : "FREEZE";
    case "round_live":
      if (!round) return "LIVE";
      return m.scoreLimit > 0 ? `${round} \u00b7 FIRST TO ${m.scoreLimit}` : round;
    case "round_end":
      return round ? `${round} \u00b7 OVER` : "ROUND OVER";
    case "ended":
      return "MATCH OVER";
  }
}

/** The big timer is shown for every phase that has a running clock; countdowns live in the label. */
export function timerText(m: MatchHudView, timeLeftSec: number | null = m.timeLeftSec): string {
  if (m.phase === "warmup" || m.phase === "countdown" || timeLeftSec == null) return "";
  return formatTimer(timeLeftSec);
}

/** Phases whose clock keeps running between `setMatch` calls. */
export function isClockRunning(phase: MatchHudView["phase"]): boolean {
  return phase === "countdown" || phase === "live" || phase === "round_freeze" || phase === "round_live";
}

/**
 * Radius in CSS pixels on screen of a spread cone (`spreadDeg` = cone half-angle) at the crosshair.
 * The projection scale follows the renderer (`verticalFovDeg`): the horizontal FOV applies at 16:9
 * and narrower windows (`aspect` below 16:9) get the wider vertical FOV that gives, so a bullet
 * landing `spreadDeg` off the crosshair lands exactly on the drawn crosshair arm.
 */
export function spreadToPixels(spreadDeg: number, hFovDeg: number, viewportHeight: number, aspect = REF_ASPECT): number {
  if (!(spreadDeg > 0) || !(hFovDeg > 0) || !(viewportHeight > 0)) return 0;
  const tanHalfH = Math.tan(Math.min(hFovDeg, 170) * DEG * 0.5);
  const tanHalfV = tanHalfH / Math.min(Math.max(aspect, 0.25), REF_ASPECT);
  const tanSpread = Math.tan(Math.min(spreadDeg, 60) * DEG);
  return (tanSpread / tanHalfV) * (viewportHeight * 0.5);
}

/** Wrap an angle to (-PI, PI]. Non-finite input gives 0. */
export function normalizeAngle(rad: number): number {
  if (!Number.isFinite(rad)) return 0;
  let r = rad % TAU;
  if (r > Math.PI) r -= TAU;
  else if (r <= -Math.PI) r += TAU;
  return r + 0; // never -0
}

/**
 * CSS rotation (degrees, clockwise positive) of a damage arc that is drawn at the top of its ring.
 * `dirRad` is the damage source direction relative to the view: 0 ahead, + to the left.
 */
export function damageArcRotationDeg(dirRad: number): number {
  return normalizeAngle(-dirRad) / DEG + 0;
}

/** Arc opacity for a hit of `amount` damage: light chip damage is faint, 60+ is fully opaque. */
export function damageArcStrength(amount: number): number {
  if (!Number.isFinite(amount)) return 0.35;
  return Math.min(1, Math.max(0.35, 0.35 + amount / 60));
}

const WEAPON_LABELS: Readonly<Record<string, string>> = {
  vx9: "VX-9",
  kr7: "KR-7",
  br3: "BR-3",
  hx12: "HX-12",
  dm2: "DM-2",
  lx44: "LX-44",
  p17: "P-17",
  vp4: "VP-4",
  hc50: "HC-50",
  knife: "KNIFE",
  throwing_knife: "THROWING KNIFE",
  frag: "FRAG",
  flash: "FLASH",
  smoke: "SMOKE",
  impulse: "IMPULSE",
  bomb: "BOMB",
  world: "WORLD",
  suicide: "SUICIDE",
};

/** Short uppercase label for a weapon/equipment id ("kr7" -> "KR-7", unknown ids are upper-cased). */
export function weaponLabel(id: string): string {
  return WEAPON_LABELS[id] ?? id.replace(/_/g, " ").toUpperCase();
}

export function teamName(team: TeamTag): string {
  return team === "a" ? "ORANGE" : team === "b" ? "BLUE" : "";
}

/** 0 good, 1 ok, 2 bad (CSS tiers of the ping readouts). */
export function pingTier(ms: number): 0 | 1 | 2 {
  return ms < 60 ? 0 : ms < 120 ? 1 : 2;
}

/** Round `v` to a multiple of `step` (cache keys: touch the DOM only when this changes). */
export function quantize(v: number, step: number): number {
  return Math.round(v / step) * step;
}

export function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

export function compareRows(a: ScoreboardRow, b: ScoreboardRow): number {
  return b.score - a.score || b.kills - a.kills || a.deaths - b.deaths || a.id - b.id;
}

/**
 * Fill `out` with the rows of one side, best first. "all" takes everything, "a" every row that is not
 * team b (team a and unassigned), "b" only team b. Reuses `out`.
 */
export function collectSortedRows(rows: readonly ScoreboardRow[], side: "all" | "a" | "b", out: ScoreboardRow[]): ScoreboardRow[] {
  out.length = 0;
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i]!;
    if (side === "all" || (side === "b") === (r.team === "b")) out.push(r);
  }
  out.sort(compareRows);
  return out;
}

/** out = [aliveA, totalA, aliveB, totalB]; unassigned rows count for neither team. */
export function countTeamAlive(rows: readonly ScoreboardRow[], out: Int32Array): void {
  out.fill(0);
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i]!;
    const base = r.team === "a" ? 0 : r.team === "b" ? 2 : -1;
    if (base < 0) continue;
    out[base + 1]++;
    if (r.alive) out[base]++;
  }
}
